/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  type CustomCircleStyle,
  type CustomLineStyle,
  type CustomRectStyle,
  type CustomSeriesRenderItemAPI,
  type CustomTextStyle,
  type CustomSeriesRenderItemParams
} from 'echarts';
import { util, graphic } from 'echarts/core';
import type { DvMarkerOption } from '../../../../core/option';
import { isArray } from '../../../../util/utils';
import { BoundingRect } from '../../../util/coord';
import type { DvMarkerData, markerRenderItemReturn } from '../type';

// flag图形专用style配置 要改成echarts的配置项
export interface FlagStyleProps {
  lineStyle: CustomLineStyle & { style: { lineHeight: number } };
  circleStyle: CustomCircleStyle;
  rectStyle: CustomRectStyle;
  textStyle: CustomTextStyle;
}

function getTextRect(textStyle: CustomTextStyle) {
  const content = new graphic.Text(textStyle);
  const { x, y, width, height } = content.getBoundingRect();
  const padding = content.style.padding || [0, 0, 0, 0];
  return {
    x,
    y,
    // 满足富文本内边距设置
    width: width + padding[1] + padding[3],
    height: height + padding[0] + padding[2]
  };
}

const handleRedius = (
  lineHeight: number,
  rectWidth: number,
  radius: unknown
): [unknown, unknown, unknown, unknown] | unknown => {
  const r = radius || 2;
  return [r, r, r, r];
};

/**
 * @implements
 * 1. 水平方向： 左侧能放下就放左侧 放不下放右侧
 * 2. 竖直方向：
 *  2.1 中轴向上，默认放下面，下面放不下才放上面
 *  2.2 中轴向下，默认放上面，上面放不下才放下面
 * @param lHeight
 * @param rWidth
 * @param point
 * @param width
 * @param height
 * @returns
 */
const handleFlagPosition = (
  lHeight: number,
  rWidth: number,
  point: [number, number],
  gridRect: BoundingRect
) => {
  const { x: gridX, y: gridY, width: gridWidth, height: gridHeight } = gridRect;
  const horizontal = point[0] - rWidth > gridX ? -1 : 1;
  const vertical = point[1] > gridHeight / 2 + gridY ? 1 : -1;
  // if ()
  const lineHeight = lHeight * vertical;
  const rectWidth = rWidth * horizontal;

  return {
    lineHeight,
    rectWidth
  };
};

// eslint-disable-next-line complexity
export function flag(
  data: DvMarkerData,
  seriesOption: DvMarkerOption,
  api: CustomSeriesRenderItemAPI,
  params: CustomSeriesRenderItemParams,
  dataIndex: number
): markerRenderItemReturn {
  const { itemStyle } = data;
  let dvAnimationConfig = util.clone(seriesOption.dvAnimationConfig || {});
  const { circleStyle, lineStyle, rectStyle, textStyle } = itemStyle;
  const { lineHeight: lHeight, lineWidth } = lineStyle.style;
  const coords = [api.value(0), api.value(1)];
  const point = api.coord([coords[0], coords[1]]) as [number, number];
  // !!!!!!!!   todo 缺少临界判断
  const { r: radius = 0 } = rectStyle?.shape || {};
  let rWidth = rectStyle.shape.width;
  let rectHeight = rectStyle.shape.height;
  const { text: textContent, x: textOffsetX, y: textOffsetY } = textStyle.style;

  const gridRect = params.coordSys;
  // 自适应文本宽高 通过预渲染text获取包围矩形宽高
  if (typeof rWidth === 'undefined' || typeof rectHeight === 'undefined') {
    const { width: tWidth, height: tHeight } = getTextRect(textStyle);
    rWidth = tWidth;
    rectHeight = tHeight;
  }
  // 调整旗子标签的朝向
  const { lineHeight, rectWidth } = handleFlagPosition(lHeight, rWidth, point, gridRect);
  // 调整旗子标签的动画参数 主要是为流水线显示设置延迟时间
  if (
    dvAnimationConfig.dvStreamAnimation &&
    typeof dvAnimationConfig?.enterAnimation?.delay === 'undefined'
  ) {
    const categoryIndex = coords[0];
    const { duration } = dvAnimationConfig.enterAnimation;
    const streamDelay = duration * categoryIndex;
    dvAnimationConfig.enterAnimation.delay = streamDelay;
    const { dvStreamAnimation, ...othersConfig } = dvAnimationConfig;
    dvAnimationConfig = othersConfig;
  }
  const halfLineWidth = lineWidth / 2;
  const rectRadius = isArray(radius) ? radius : handleRedius(lineHeight, rectWidth, radius);
  const { shape, ...elRectStyle } = rectStyle;
  const { style: { x, y, ...otherTextStyle } = {}, ...elTextStyle } = textStyle;
  elTextStyle.style = otherTextStyle;

  const circle = {
    type: 'circle',
    extra: {
      dvid: `flag_circle_${dataIndex}`
    },
    x: point[0],
    y: point[1],
    ...dvAnimationConfig
  };
  const line = {
    type: 'line',
    extra: {
      dvid: `flag_line_${dataIndex}`
    },
    shape: {
      x1: point[0],
      y1: point[1],
      x2: point[0],
      y2: point[1] - lineHeight
    },
    ...dvAnimationConfig
  };
  const rect = {
    type: 'rect',
    extra: {
      dvid: `flag_rect_${dataIndex}`
    },
    shape: {
      x: 0,
      y: 0,
      width: rectWidth,
      r: rectRadius,
      height: rectHeight
    },
    ...dvAnimationConfig
  };
  const text = {
    type: 'text',
    extra: {
      dvid: `flag_text_${dataIndex}`
    },
    x: 0,
    y: 0,
    style: {
      x: rectWidth > 0 ? textOffsetX : rectWidth + textOffsetX,
      // 消除text计算包围盒多余的高度 满足padding
      // 具体原因待查明
      y: textOffsetY || 1,
      text: textContent
    },
    ...dvAnimationConfig
  };
  util.merge(circle, circleStyle);
  util.merge(line, lineStyle);
  util.merge(rect, elRectStyle);
  util.merge(text, elTextStyle, true);

  return {
    $mergeChildren: false,
    type: 'group',
    extra: {
      dvid: `flag_group_outside_${dataIndex}`
    },
    children: [
      circle,
      {
        type: 'group',
        extra: {
          dvid: `flag_group_middle_${dataIndex}`
        },
        children: [
          line,
          {
            type: 'group',
            extra: {
              dvid: `flag_group_inside_${dataIndex}`
            },
            x: point[0],
            y: point[1] - lineHeight + (lineHeight > 0 ? -rectHeight : 0),
            children: [rect, text]
          }
        ]
      }
    ],
    silent: true
  };
}
